<template>
  <!-- 客户分组管理模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleCancel"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              客户分组管理
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              创建和管理客户分组，方便客户分类
            </p>
          </div>
          <button
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200 mb-6">
          <nav class="flex space-x-4 sm:space-x-8">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
                'flex-1 sm:flex-initial text-center sm:text-left',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- 创建分组 Tab -->
        <div v-show="activeTab === 'create'" class="space-y-6 mb-6">
          <div>
            <label class="text-sm font-medium text-gray-700 mb-3 block">创建新分组</label>
            <div class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组名称 <span class="text-red-500">*</span></label>
                  <input
                    v-model="newGroup.name"
                    type="text"
                    placeholder="输入分组名称"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    :disabled="submitting"
                    @keyup.enter="handleCreateGroup"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组描述</label>
                  <input
                    v-model="newGroup.description"
                    type="text"
                    placeholder="描述该分组用途（可选）"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    :disabled="submitting"
                  />
                </div>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组颜色</label>
                  <div class="flex items-center gap-3">
                    <input
                      v-model="newGroup.color"
                      type="color"
                      class="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                      :disabled="submitting"
                    />
                    <span class="text-sm text-gray-600">选择分组标识颜色</span>
                  </div>
                </div>
                <div>
                  <button
                    @click="handleCreateGroup"
                    :disabled="!newGroup.name.trim() || submitting"
                    class="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {{ submitting ? '创建中...' : '创建分组' }}
                  </button>
                </div>
              </div>

              <div v-if="Object.keys(errors).length > 0" class="mt-4">
                <p v-for="(error, key) in errors" :key="key" class="text-sm text-red-600">{{ error }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 分组管理 Tab -->
        <div v-show="activeTab === 'manage'" class="space-y-6 mb-6">
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">
                客户分组 
                <span class="text-sm text-gray-500">({{ filteredGroups.length }}{{ searchQuery ? ' / ' + groups.length : '' }})</span>
              </label>
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索分组..."
                  class="w-32 sm:w-48 h-8 pl-8 pr-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <svg class="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div v-if="loading" class="flex flex-col items-center justify-center py-8">
              <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>

            <div v-else-if="filteredGroups.length === 0" class="flex flex-col items-center justify-center text-center text-gray-500 py-8">
              <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p class="text-lg font-medium mb-2">
                {{ searchQuery ? '未找到匹配的分组' : '暂无分组' }}
              </p>
              <p class="text-sm">
                {{ searchQuery ? '尝试使用其他关键词搜索' : '点击"创建分组"标签页创建第一个客户分组' }}
              </p>
            </div>

            <div v-else class="max-h-[30vh] overflow-y-auto pr-2 -mr-2" style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;">
              <div class="grid grid-cols-2 gap-2 items-start content-start">
                <div
                  v-for="group in filteredGroups"
                  :key="group.id"
                  class="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors min-h-[40px]"
                >
                  <!-- 分组显示 -->
                  <div class="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-1 overflow-hidden">
                    <span
                      :style="{ backgroundColor: group.color || '#3B82F6' }"
                      class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 text-white max-w-[120px] sm:max-w-none overflow-hidden"
                      :title="group.description || group.name"
                    >
                      <span class="truncate">{{ group.name }}</span>
                    </span>
                    <span class="text-xs text-gray-500 flex-shrink-0">
                      {{ group.customer_count || 0 }}
                    </span>
                  </div>

                  <!-- 操作按钮 -->
                  <div class="flex items-center space-x-1 ml-2 flex-shrink-0">
                    <button
                      @click="handleEditGroup(group)"
                      class="text-blue-600 hover:text-blue-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center"
                      title="编辑分组"
                    >
                      ✏️
                    </button>
                    <button
                      v-if="!isSubAccount"
                      @click="handleDeleteGroup(group)"
                      :disabled="group.customer_count > 0"
                      class="text-red-600 hover:text-red-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center disabled:text-gray-400 disabled:cursor-not-allowed"
                      :title="group.customer_count > 0 ? '该分组下还有客户，无法删除' : '删除分组'"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部操作按钮 -->
        <div class="flex justify-end pt-6 border-t border-gray-200">
          <button
            @click="handleCancel"
            type="button"
            class="inline-flex items-center px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 编辑分组模态框 -->
  <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50">
    <div class="bg-white rounded-lg shadow-xl w-[90vw] max-w-[90vw] sm:max-w-lg mx-auto">
      <div class="p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900">编辑客户分组</h3>
          <button
            @click="handleCloseEditModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>
        
        <!-- 编辑表单 -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">分组名称</label>
            <input
              v-model="editingGroup.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="输入分组名称"
              :disabled="submitting"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">分组描述</label>
            <input
              v-model="editingGroup.description"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="输入分组描述（可选）"
              :disabled="submitting"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">分组颜色</label>
            <div class="flex items-center gap-3">
              <input
                v-model="editingGroup.color"
                type="color"
                class="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                :disabled="submitting"
              />
              <span class="text-sm text-gray-600">选择分组标识颜色</span>
            </div>
          </div>
        </div>
        
        <!-- 操作按钮 -->
        <div class="flex justify-end space-x-3 mt-6">
          <button
            @click="handleCloseEditModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            @click="handleUpdateGroup"
            :disabled="!editingGroup.name.trim() || submitting"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ submitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { customerGroupService } from '@/services/customerService'
import { useAuthStore } from '@/stores/auth'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()
const authStore = useAuthStore()

// 子账户：仅可编辑已分配分组，不能创建/删除
const isSubAccount = computed(() => authStore.user?.account_type === 'sub')

// 状态管理
const loading = ref(false)
const submitting = ref(false)
const groups = ref([])
const searchQuery = ref('')
const errors = ref({})

// Tab 管理（子账户不显示“创建分组”）
const activeTab = ref('create')
const tabs = computed(() => {
  const list = [{ id: 'manage', name: '分组管理' }]
  if (!isSubAccount.value) {
    list.unshift({ id: 'create', name: '创建分组' })
  }
  return list
})

// 新分组表单
const newGroup = reactive({
  name: '',
  description: '',
  color: '#3B82F6'  // 默认蓝色
})

// 编辑分组
const editingGroup = ref(null)
const showEditModal = ref(false)

// 筛选分组
const filteredGroups = computed(() => {
  if (!searchQuery.value) {
    return groups.value
  }
  return groups.value.filter(group => 
    group.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    (group.description && group.description.toLowerCase().includes(searchQuery.value.toLowerCase()))
  )
})

// 监听显示状态
watch(() => props.show, (newValue) => {
  if (newValue) {
    activeTab.value = isSubAccount.value ? 'manage' : 'create'
    resetForm()
    loadGroups()
  }
})

// 重置表单
const resetForm = () => {
  newGroup.name = ''
  newGroup.description = ''
  newGroup.color = '#3B82F6'
  errors.value = {}
  searchQuery.value = ''
}

// 关闭模态框
const handleCancel = () => {
  activeTab.value = isSubAccount.value ? 'manage' : 'create'
  resetForm()
  emit('close')
}

// 加载分组
const loadGroups = async () => {
  try {
    loading.value = true
    const response = await customerGroupService.getGroups()
    groups.value = response.data?.results || response.data || []
  } catch (error) {
    console.error('Failed to load groups:', error)
    toast.error('加载分组失败')
  } finally {
    loading.value = false
  }
}

// 创建分组
const handleCreateGroup = async () => {
  if (!newGroup.name.trim()) {
    errors.value = { name: '分组名称不能为空' }
    return
  }

  try {
    submitting.value = true
    errors.value = {}
    
    const groupData = {
      name: newGroup.name.trim(),
      description: newGroup.description.trim() || '',
      color: newGroup.color
    }
    
    const result = await customerGroupService.createGroup(groupData)
    if (result.success) {
      toast.success('分组创建成功')
    } else {
      throw new Error(result.message || '创建失败')
    }
    
    // 重置表单并切换到管理页面
    resetForm()
    activeTab.value = 'manage'
    await loadGroups()
    emit('success')
  } catch (error) {
    console.error('Failed to create group:', error)
    if (error.response?.data) {
      errors.value = error.response.data
    } else {
      toast.error('创建分组失败')
    }
  } finally {
    submitting.value = false
  }
}

// 编辑分组
const handleEditGroup = (group) => {
  editingGroup.value = { ...group }
  showEditModal.value = true
}

// 关闭编辑模态框
const handleCloseEditModal = () => {
  editingGroup.value = null
  showEditModal.value = false
  errors.value = {}
}

// 更新分组
const handleUpdateGroup = async () => {
  if (!editingGroup.value.name.trim()) {
    errors.value = { name: '分组名称不能为空' }
    return
  }

  try {
    submitting.value = true
    errors.value = {}
    
    const groupData = {
      name: editingGroup.value.name.trim(),
      description: editingGroup.value.description?.trim() || '',
      color: editingGroup.value.color || '#3B82F6'
    }
    
    const result = await customerGroupService.updateGroup(editingGroup.value.id, groupData)
    if (result.success) {
      toast.success('分组更新成功')
    } else {
      throw new Error(result.message || '更新失败')
    }
    
    handleCloseEditModal()
    await loadGroups()
    emit('success')
  } catch (error) {
    console.error('Failed to update group:', error)
    if (error.response?.data) {
      errors.value = error.response.data
    } else {
      toast.error('更新分组失败')
    }
  } finally {
    submitting.value = false
  }
}

// 删除分组
const handleDeleteGroup = async (group) => {
  if (group.customer_count > 0) {
    toast.error('该分组下还有客户，无法删除')
    return
  }

  if (!confirm(`确定要删除分组"${group.name}"吗？`)) {
    return
  }

  try {
    const result = await customerGroupService.deleteGroup(group.id)
    if (result.success) {
      toast.success('分组删除成功')
      await loadGroups()
      emit('success')
    } else {
      throw new Error(result.message || '删除失败')
    }
  } catch (error) {
    console.error('Failed to delete group:', error)
    toast.error(error.message || '删除分组失败')
  }
}
</script>